import { useCallback, useEffect, useRef, useState } from "react";
import { api, broadcast, getChannel } from "../lib/api";
import { BROADCAST_EVENTS, DEFAULT_STATE } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

const POLL_INTERVAL_MS = 2000;

export const useQueueState = ({ poll = false } = {}) => {
  const { user } = useAuth();
  const [state, setState] = useState(DEFAULT_STATE);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [lastPolledAt, setLastPolledAt] = useState(null);
  const [configUpdatedAt, setConfigUpdatedAt] = useState(null);
  const lastUpdatedRef = useRef(null);
  const onChangeListenersRef = useRef([]);

  const fetchState = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const { data } = await api.get("/state");
      setState(data.state || DEFAULT_STATE);
      setLastPolledAt(Date.now());
      if (data.config_updated_at !== undefined) {
        setConfigUpdatedAt(data.config_updated_at);
      }
      if (data.updated_at && data.updated_at !== lastUpdatedRef.current) {
        const prev = lastUpdatedRef.current;
        lastUpdatedRef.current = data.updated_at;
        setUpdatedAt(data.updated_at);
        // Fire change listeners (only after first hydrate)
        if (prev) {
          onChangeListenersRef.current.forEach((fn) => fn(data.state, data.updated_at));
        }
      }
    } catch {
      /* swallow — leave lastPolledAt untouched so heartbeat goes offline naturally */
    }
  }, [user?.company_id]);

  // Initial fetch + polling
  useEffect(() => {
    fetchState();
    if (!poll) return undefined;
    const id = setInterval(fetchState, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchState, poll]);

  // Listen to BroadcastChannel for same-browser instant sync
  useEffect(() => {
    const ch = getChannel();
    if (!ch) return undefined;
    const handler = (e) => {
      const { type } = e.data || {};
      if (
        type === BROADCAST_EVENTS.TICKET_CALLED ||
        type === BROADCAST_EVENTS.STATE_UPDATED ||
        type === BROADCAST_EVENTS.RECALL ||
        type === BROADCAST_EVENTS.RESET
      ) {
        fetchState();
      }
    };
    ch.addEventListener("message", handler);
    return () => ch.removeEventListener("message", handler);
  }, [fetchState]);

  const onStateChange = useCallback((fn) => {
    onChangeListenersRef.current.push(fn);
    return () => {
      onChangeListenersRef.current = onChangeListenersRef.current.filter((x) => x !== fn);
    };
  }, []);

  const callNext = useCallback(async (prefix, desk) => {
    const { data } = await api.post("/state/call-next", { prefix, desk });
    setState(data.state);
    lastUpdatedRef.current = data.updated_at;
    setUpdatedAt(data.updated_at);
    broadcast(BROADCAST_EVENTS.TICKET_CALLED, { ticket: data.ticket });
    return data.ticket;
  }, []);

  const callManual = useCallback(async (code, desk) => {
    const { data } = await api.post("/state/call-manual", { code, desk });
    setState(data.state);
    lastUpdatedRef.current = data.updated_at;
    setUpdatedAt(data.updated_at);
    broadcast(BROADCAST_EVENTS.TICKET_CALLED, { ticket: data.ticket });
    return data.ticket;
  }, []);

  const recall = useCallback(async () => {
    const { data } = await api.post("/state/recall");
    if (data.state) setState(data.state);
    lastUpdatedRef.current = data.updated_at;
    setUpdatedAt(data.updated_at);
    if (data.ticket) broadcast(BROADCAST_EVENTS.RECALL, { ticket: data.ticket });
  }, []);

  const skip = useCallback(async () => {
    const { data } = await api.post("/state/skip");
    setState(data.state);
    lastUpdatedRef.current = data.updated_at;
    setUpdatedAt(data.updated_at);
    broadcast(BROADCAST_EVENTS.STATE_UPDATED);
  }, []);

  const reset = useCallback(async () => {
    const { data } = await api.post("/state/reset");
    setState(data.state);
    lastUpdatedRef.current = data.updated_at;
    setUpdatedAt(data.updated_at);
    broadcast(BROADCAST_EVENTS.RESET);
  }, []);

  return { state, updatedAt, lastPolledAt, configUpdatedAt, callNext, callManual, recall, skip, reset, onStateChange, fetchState };
};
